import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, X, FileText } from 'lucide-react';

import { useApi } from '../lib/api.js';
import { Button } from './ui/Button.jsx';
import { Input } from './ui/Form.jsx';
import { Modal } from './ui/Modal.jsx';
import { cn } from '../lib/cn.js';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE_MB = 10;

/**
 * Document upload flow for a driver or vehicle.
 *
 * Three modes, determined by the incoming docType:
 *   1. A required doc type (driving_licence, phd_licence, phd_badge, mot, etc.):
 *      - docLabel comes from the parent and is shown as-is
 *      - custom_label is not asked for
 *   2. 'other' doc type:
 *      - user must also enter a document name (stored as custom_label)
 *      - expiry is optional
 *   3. profile_picture / v5:
 *      - no expiry needed (requiresExpiry=false)
 *
 * R2 direct-upload flow:
 *   1. Ask backend for signed URL
 *   2. PUT file directly to R2
 *   3. Confirm to backend (creates documents row)
 */
export function DocumentUploadModal({
    ownerType,
    ownerId,
    docType,
    docLabel,
    requiresExpiry = true,
    expiryOptional = false, // for 'other' docs where expiry might not apply
    open,
    onClose,
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [expiresAt, setExpiresAt] = useState('');
    const [customLabel, setCustomLabel] = useState('');
    const [fileError, setFileError] = useState('');
    const [labelError, setLabelError] = useState('');

    const isOther = docType === 'other';

    const reset = () => {
        setFile(null);
        setExpiresAt('');
        setCustomLabel('');
        setFileError('');
        setLabelError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const close = () => {
        reset();
        onClose();
    };

    const validateFile = (f) => {
        if (!ALLOWED_TYPES.includes(f.type)) {
            return 'Only PDF, JPEG, or PNG files are allowed';
        }
        if (f.size > MAX_SIZE_MB * 1024 * 1024) {
            return `File is too large (max ${MAX_SIZE_MB} MB)`;
        }
        return null;
    };

    const handleFileSelect = (f) => {
        const err = validateFile(f);
        if (err) {
            setFileError(err);
            setFile(null);
            return;
        }
        setFileError('');
        setFile(f);
    };

    const uploadMutation = useMutation({
        mutationFn: async () => {
            if (!file) throw new Error('No file selected');
            if (isOther && !customLabel.trim()) {
                setLabelError('Document name is required');
                throw new Error('Document name is required');
            }
            if (requiresExpiry && !expiryOptional && !expiresAt) {
                throw new Error('Expiry date is required');
            }

            // Step 1: signed URL
            const { upload_url, r2_key } = await api.post('/documents/upload-url', {
                owner_type: ownerType,
                owner_id: ownerId,
                doc_type: docType,
                original_filename: file.name,
                mime_type: file.type,
            });

            // Step 2: PUT to R2
            const putRes = await fetch(upload_url, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });
            if (!putRes.ok) {
                throw new Error(`Upload to storage failed (${putRes.status})`);
            }

            // Step 3: confirm, creates the row
            const confirmPayload = {
                owner_type: ownerType,
                owner_id: ownerId,
                doc_type: docType,
                r2_key,
                file_name: file.name,
                mime_type: file.type,
                file_size: file.size,
                expires_at: expiresAt || null,
            };
            if (isOther) {
                confirmPayload.custom_label = customLabel.trim();
            }

            const doc = await api.post('/documents', confirmPayload);
            return doc;
        },
        onSuccess: () => {
            toast.success(`${isOther ? customLabel.trim() : docLabel} uploaded`);
            queryClient.invalidateQueries({ queryKey: ['driver', ownerId] });
            queryClient.invalidateQueries({ queryKey: ['vehicle', ownerId] });
            queryClient.invalidateQueries({ queryKey: ['documents', ownerType, ownerId] });
            close();
        },
        onError: (err) => {
            toast.error(err.message || 'Upload failed');
        },
    });

    const title = isOther ? 'Upload additional document' : `Upload ${docLabel}`;
    const showExpiryField = requiresExpiry;
    // For 'other' docs we show expiry as optional; for required-type docs it's mandatory if requiresExpiry
    const expiryRequired = requiresExpiry && !expiryOptional;

    const canSubmit = file
        && (!isOther || customLabel.trim().length > 0)
        && (!expiryRequired || expiresAt);

    return (
        <Modal open={open} onClose={close} title={title} size="md">
            <div className="space-y-4">
                {isOther && (
                    <Input
                        label="Document name"
                        value={customLabel}
                        onChange={(e) => {
                            setCustomLabel(e.target.value);
                            if (labelError) setLabelError('');
                        }}
                        placeholder="e.g. Passport, Right to Work, DBS certificate"
                        required
                        error={labelError}
                        maxLength={100}
                        hint="This name is how the document appears in the chauffeur profile and compliance PDF."
                    />
                )}

                <FileDropzone
                    file={file}
                    onSelect={handleFileSelect}
                    onClear={reset}
                    error={fileError}
                    inputRef={fileInputRef}
                />

                {showExpiryField && (
                    <Input
                        label={expiryRequired ? 'Expiry date' : 'Expiry date (optional)'}
                        type="date"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        required={expiryRequired}
                        min={new Date().toISOString().slice(0, 10)}
                        hint={
                            expiryRequired
                                ? 'Expiry reminders will be sent at 7 days and 1 day before this date.'
                                : "Leave blank if the document doesn't expire."
                        }
                    />
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={close} disabled={uploadMutation.isPending}>Cancel</Button>
                    <Button
                        onClick={() => uploadMutation.mutate()}
                        disabled={!canSubmit}
                        loading={uploadMutation.isPending}
                    >
                        <Upload className="h-4 w-4" />
                        Upload
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function FileDropzone({ file, onSelect, onClear, error, inputRef }) {
    const [isDragging, setIsDragging] = useState(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) onSelect(f);
    };

    if (file) {
        return (
            <div className="flex items-center justify-between gap-3 p-3 bg-paper-hover rounded-md border border-paper-border">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-9 w-9 rounded-md bg-paper-card flex items-center justify-center text-ink-muted flex-shrink-0">
                        <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink truncate">{file.name}</div>
                        <div className="text-xs text-ink-subtle">{formatBytes(file.size)}</div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="text-ink-muted hover:text-status-danger transition-colors flex-shrink-0"
                    aria-label="Remove file"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <div>
            <label
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={cn(
                    'block cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors',
                    isDragging
                        ? 'border-ink bg-paper-hover'
                        : 'border-paper-border hover:border-ink-faint hover:bg-paper-hover/50',
                    error && 'border-status-danger',
                )}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(',')}
                    onChange={(e) => { if (e.target.files?.[0]) onSelect(e.target.files[0]); }}
                    className="sr-only"
                />
                <Upload className="h-6 w-6 mx-auto text-ink-subtle" />
                <div className="mt-2 text-sm font-medium text-ink">Click or drag file to upload</div>
                <div className="mt-1 text-xs text-ink-subtle">PDF, JPEG, or PNG · Max {MAX_SIZE_MB} MB</div>
            </label>
            {error && <p className="field-error">{error}</p>}
        </div>
    );
}

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
